import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/components/services/lib/toast';
import {
  Music, ChevronLeft, ChevronRight, Calendar, Check, X, Clock, ExternalLink, AlertCircle, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { HighlightRing } from '@/components/shared/SharedTasksSection';
import { formatTimeAgo } from '@/utils/timeFormat';
import { useServicesMode } from '@/components/services/hooks/useServicesMode';
import { svcGet, svcPost, svcDelete, errorText } from '@/components/services/lib/api';
import { fmtDate, fmtTime, passedBy, churchNow, zoneSuffix } from '@/components/services/lib/format';
import StatusPill from '@/components/services/components/StatusPill';
import KeySelect from '@/components/services/components/KeySelect';
import SongLinks from '@/components/services/components/SongLinks';
import ServiceComments from '@/components/services/components/ServiceComments';
import ServicesFooter from '@/components/services/components/ServicesFooter';
import BlockoutCalendar, { fmtBlockoutRange } from '@/components/services/components/BlockoutCalendar';
import LoadFailed from '@/components/services/components/LoadFailed';

// Team Comms "Services" view for /team (Section 6 Phase 5, D22): the member's
// own services from the same endpoints as the mobile tab (Section 11): the list
// (GET /mine), the detail (GET /mine/{id}), Accept / Decline (respond, one row
// per call, ruling Q1), comments, block-out dates with "Decline it too?"
// (B-01), and the read-only Planning Center mirror in `pco` mode (11.10).
// Refreshes on window focus and when a service notification arrives
// (`notifStamp`), never on a timer.

const CARD = 'rounded-2xl border border-slate-200 dark:border-white/10 bg-card p-4';
const HEADING = 'text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium mb-3';
const ROW = 'bg-slate-50 dark:bg-[#34373c] rounded-lg px-3 py-2';

function timesLine(s, tz) {
  const parts = [];
  const zone = zoneSuffix(tz);   // 11.3: once, after the service time, when the device zone differs (Fix 6D D6)
  if (s.service_time) parts.push(zone ? `${fmtTime(s.service_time)} ${zone}` : fmtTime(s.service_time));
  if (s.soundcheck_time) parts.push(`Soundcheck ${fmtTime(s.soundcheck_time)}`);
  if (s.rehearsal_date && s.rehearsal_time) parts.push(`Rehearsal ${fmtDate(s.rehearsal_date, 'EEE, MMM d')} ${fmtTime(s.rehearsal_time)}`);
  return parts.join(' · ');
}

const positionsOf = (rows) => [...new Set((rows || []).map((a) => a.position))].join(' and ');

// PC9 (Fix 6F F5): one "Decline it too?" entry per service, always led by the
// SERVICE's date and time; a rehearsal-only overlap adds the rehearsal's in
// parentheses (its time left out when the service has none).
function overlapEntry(g) {
  const head = [fmtDate(g.date, 'EEE, MMM d'), fmtTime(g.serviceTime), g.positions.join(' and ')].filter(Boolean).join(' · ');
  if (!g.rehearsal) return head;
  const reh = [fmtDate(g.rehearsalDate, 'EEE, MMM d'), fmtTime(g.rehearsalTime)].filter(Boolean).join(' ');
  return `${head} (rehearsal ${reh})`;
}

function StatusIcon({ status }) {
  if (status === 'accepted' || status === 'confirmed') return <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 flex-shrink-0" />;
  if (status === 'declined') return <XCircle className="w-[18px] h-[18px] text-red-400 flex-shrink-0" />;
  return <Clock className="w-[18px] h-[18px] text-amber-400 flex-shrink-0" />;
}

// ── Decline sheet (11.6 copy; the leader is "your leader" on web, the detail has no leader name) ──
function DeclineDialog({ item, busy, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  useEffect(() => { setReason(''); }, [item?.service_id]);
  return (
    <AlertDialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent data-testid="services-view-decline-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Can't make {fmtDate(item?.date, 'MMM d')}?</AlertDialogTitle>
          <AlertDialogDescription>
            Your leader will be told right away and can find someone else. This service will leave your list.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Input value={reason} onChange={(e) => setReason(e.target.value.slice(0, 140))} placeholder="Reason (optional)" className="h-9" data-testid="services-view-decline-reason" />
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-right tabular-nums" data-testid="services-view-decline-reason-counter">{reason.length}/140</p>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="services-view-decline-cancel">Never mind</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={(e) => { e.preventDefault(); onConfirm(reason.trim() || undefined); }}
            disabled={busy}
            data-testid="services-view-decline-confirm"
          >
            Decline
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Song card (11.5): key dropdown is local only and re-splits the links (L13-4) ──
function SongCard({ song, serviceDate, onClose }) {
  const [key, setKey] = useState(song?.key || null);
  useEffect(() => { setKey(song?.key || null); }, [song]);
  return (
    <Sheet open={!!song} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="services-view-song-card">
        <SheetHeader>
          <SheetTitle className="text-left pr-8">{song?.title}</SheetTitle>
          <SheetDescription className="text-left">{[song?.artist, song?.leader_name ? `Led by ${song.leader_name}` : null].filter(Boolean).join(' · ') || ' '}</SheetDescription>
        </SheetHeader>
        {song && (
          <div className="mt-5 space-y-5">
            <div className="flex items-center gap-2 flex-wrap text-sm text-slate-700 dark:text-slate-200">
              <span>This {fmtDate(serviceDate, 'EEEE')} in</span>
              <KeySelect value={key} onChange={setKey} data-testid="services-view-song-key" />
              {song.key && key !== song.key && (
                <button type="button" onClick={() => setKey(song.key)} className="text-xs text-teal-700 dark:text-teal-300 underline underline-offset-2" data-testid="services-view-song-key-reset">Back to {song.key}</button>
              )}
            </div>
            {song.note && <p className="text-sm text-slate-500 dark:text-slate-400">{song.note}</p>}
            <SongLinks links={[...(song.links_for_key || []), ...(song.links_other || [])]} keyValue={key} searchUrl={song.youtube_search_url} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── One list card (11.3) ──
function MineCard({ item, pending, busy, onOpen, onAccept, onDecline, highlight, tz }) {
  return (
    <div className={`relative ${CARD} ${pending ? 'border-amber-300 dark:border-amber-500/40' : ''}`} data-testid={`services-view-item-${item.service_id}`}>
      {highlight && <HighlightRing className="rounded-2xl" />}
      <button type="button" onClick={() => onOpen(item.service_id)} className="w-full text-left flex items-start gap-2" data-testid={`services-view-open-${item.service_id}`}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{fmtDate(item.date, 'EEEE, MMMM d')}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{[item.title, timesLine(item, tz)].filter(Boolean).join(' · ')}</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {item.assignments.map((a) => (
              <span key={a.assignment_id} className="inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">{a.position}</span>
            ))}
            {/* Fix 8 H27 (contract MS-2.12): a Past card with a row still pending reads Pending. */}
            {!pending && <StatusPill status={item.assignments.some((a) => a.status === 'pending') ? 'pending' : 'accepted'} />}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
            {item.song_count} {item.song_count === 1 ? 'song' : 'songs'}{item.comments_count ? ` · ${item.comments_count} ${item.comments_count === 1 ? 'comment' : 'comments'}` : ''}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
      </button>
      {pending && (
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => onAccept(item)} disabled={busy} data-testid={`services-view-accept-${item.service_id}`}>
            <Check className="w-4 h-4 mr-1" /> Accept
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={() => onDecline(item)} disabled={busy} data-testid={`services-view-decline-${item.service_id}`}>
            <X className="w-4 h-4 mr-1" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Planning Center mode (11.10): read-only mirror, no Accept/Decline ──
function PcoMirror({ userId, notifStamp }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);   // Fix 6F PC10: only shown while nothing is on screen
  const load = useCallback(async () => {
    try { setData(await svcGet('/pco-mirror/mine', userId)); setFailed(false); } catch { setFailed(true); }
  }, [userId]);
  useEffect(() => { load(); }, [load, notifStamp]);
  useEffect(() => {
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);
  if (!data && failed) {
    return <LoadFailed title={'Couldn\u2019t load your Planning Center schedule'} onRetry={() => { setFailed(false); load(); }} testIdPrefix="services-view-pco" className={`${CARD} text-center py-8`} />;
  }
  if (!data) return <div className="h-24 bg-slate-100 dark:bg-[#3a3d41] rounded-2xl animate-pulse" />;
  return (
    <div className="space-y-3" data-testid="services-view-pco">
      {data.matched ? (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400">From Planning Center{data.synced_at ? ` · updated ${formatTimeAgo(data.synced_at)}` : ''}</p>
          {data.plans.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">Nothing in the next two days.</p>}
          {/* Fix 8 X2: the server sends one plan per (plan, position), so both make the key; Fix 9 C5: plus
              the row index, since one person can hold the same position text twice on one plan. */}
          {data.plans.map((p, i) => (
            <div key={`${p.plan_id}:${p.position}:${i}`} className={CARD} data-testid={`services-view-pco-plan-${p.plan_id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{fmtDate(p.date, 'EEEE, MMMM d')}{p.time ? ` · ${p.time}` : ''}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{[p.service_type, p.position].filter(Boolean).join(' · ')}</p>
                </div>
                <StatusIcon status={p.status} />
              </div>
              {p.status === 'pending' && p.plan_url && (
                <a href={p.plan_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-teal-700 dark:text-teal-300 underline underline-offset-2" data-testid={`services-view-pco-respond-${p.plan_id}`}>
                  Respond in Planning Center <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          ))}
        </>
      ) : (
        <div className={CARD} data-testid="services-view-pco-unmatched">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Your church uses Planning Center</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Ask your leader to connect you in Planning Center settings.</p>
          <a href="https://services.planningcenteronline.com/schedule" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-teal-700 dark:text-teal-300 underline underline-offset-2" data-testid="services-view-pco-schedule-link">
            Open my Planning Center schedule <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
      <div className={CARD}>
        <p className="text-sm text-slate-500 dark:text-slate-400">Vocal warm-up reminders keep working from your Planning Center schedule.</p>
      </div>
      <ServicesFooter />
    </div>
  );
}

export default function ServicesView({ user, highlightServiceId = null, highlightServiceType = null, highlightNonce = 0, onHighlightConsumed = null, notifStamp = '', onPendingChange = null }) {
  const userId = user?.id;
  const { mode, isAdmin, resolved } = useServicesMode(userId);
  const [mine, setMine] = useState(null);
  // Fix 6F F4 (PC1, PC7, A4): a first load that fails shows its failure state;
  // a failed refresh keeps what is on screen and adds nothing.
  const [mineFailed, setMineFailed] = useState(false);
  const [blockoutsFailed, setBlockoutsFailed] = useState(false);
  const [commentsFailed, setCommentsFailed] = useState(false);
  const [openId, setOpenId] = useState(highlightServiceId || null);
  // Fix 7c F9: Accept all reloads whatever detail is open when it ends (not the one at click time).
  const openIdRef = useRef(openId);
  openIdRef.current = openId;
  const listHeadingRef = useRef(null);   // Fix 7c F17: focus lands here after Accept all
  const acceptAllBtnRef = useRef(null);   // Fix 7c F17: and back here when the confirm is dismissed
  const [detail, setDetail] = useState(null);
  const [panel, setPanel] = useState(highlightServiceId ? 'detail' : 'list');   // list | detail | blockouts
  const [showPast, setShowPast] = useState(false);
  const [busy, setBusy] = useState(false);
  // Fix 7 G5: "Accept all" (K2-K4): one respond call per pending row, in order.
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const acceptingAll = useRef(false);
  const acceptAllRan = useRef(false);   // Fix 7c F17: the dialog's close must not take focus back
  const [declineFor, setDeclineFor] = useState(null);   // {service_id, date, rows:[{assignment_id}]}
  const [song, setSong] = useState(null);
  const [blockouts, setBlockouts] = useState(null);
  const [overlapAsk, setOverlapAsk] = useState(null);   // [{assignment_id, date, status, position}]
  // L11-4: one line per service (a person on two positions of one service is one service).
  const overlapGroups = useMemo(() => {
    const by = new Map();
    (overlapAsk || []).forEach((o) => {
      const g = by.get(o.service_id) || {
        date: o.date, positions: [], statuses: new Set(), rehearsal: o.kind === 'rehearsal',
        serviceTime: o.service_time, rehearsalDate: o.rehearsal_date, rehearsalTime: o.rehearsal_time,   // Fix 6F F3/F5
      };
      g.positions.push(o.position);
      g.statuses.add(o.status);
      by.set(o.service_id, g);
    });
    return [...by.values()].map((g) => ({ ...g, status: g.statuses.size === 1 ? [...g.statuses][0] : 'on' }));
  }, [overlapAsk]);
  const [ring, setRing] = useState(highlightServiceId || null);
  const topRef = useRef(null);
  // C18 (map 8): a service_comment tap opens the detail with the comments card focused.
  const commentsRef = useRef(null);
  const [focusComments, setFocusComments] = useState(highlightServiceType === 'service_comment' ? highlightServiceId : null);

  // Fix 7 G3: Team Comms' Services dot follows this view's /mine while it is open.
  // Fix 7c F10: reported straight from the load, so an answer whose reload lands
  // after the view closed still reaches the dot.
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;
  const loadMine = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await svcGet('/mine', userId);
      setMine(data);
      setMineFailed(false);
      onPendingChangeRef.current?.((data?.pending || []).length);
    } catch { setMineFailed(true); }
  }, [userId]);

  const loadDetail = useCallback(async (sid) => {
    if (!sid) return;
    try {
      setDetail(await svcGet(`/mine/${sid}`, userId));
    } catch (err) {
      // 404: declined, removed or cancelled (11.4) — back to the list.
      setDetail(null);
      setOpenId(null);
      setPanel('list');
      if (err?.status !== 404) toast.error(errorText(err));
      loadMine();
    }
  }, [userId, loadMine]);

  const loadBlockouts = useCallback(async () => {
    try { setBlockouts((await svcGet('/blockouts', userId)).blockouts || []); setBlockoutsFailed(false); } catch { setBlockoutsFailed(true); }
  }, [userId]);

  useEffect(() => { if (mode === 'simple') loadMine(); }, [mode, loadMine, notifStamp]);
  useEffect(() => { if (mode === 'simple' && openId) loadDetail(openId); }, [mode, openId, loadDetail, notifStamp]);
  useEffect(() => {
    const onFocus = () => {
      if (mode !== 'simple') return;
      loadMine();
      if (openId) loadDetail(openId);
      if (panel === 'blockouts') loadBlockouts();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [mode, openId, panel, loadMine, loadDetail, loadBlockouts]);

  // A notification tap inside Team Comms lands on that service (L6-1).
  useEffect(() => {
    if (!highlightServiceId) return;
    setOpenId(highlightServiceId);
    setPanel('detail');
    setRing(highlightServiceId);
    setFocusComments(highlightServiceType === 'service_comment' ? highlightServiceId : null);
  }, [highlightServiceId, highlightServiceType, highlightNonce]);   // Fix 7c F12: a repeat tap re-opens

  useEffect(() => {
    if (!focusComments || detail?.id !== focusComments || !commentsRef.current) return;
    commentsRef.current.scrollIntoView({ block: 'start' });
    commentsRef.current.querySelector('[data-testid="comment-input"]')?.focus({ preventScroll: true });
    const t = setTimeout(() => setFocusComments(null), 2500);
    return () => clearTimeout(t);
  }, [focusComments, detail?.id]);

  useEffect(() => {
    if (!ring) return undefined;
    const t = setTimeout(() => setRing(null), 2500);
    return () => clearTimeout(t);
  }, [ring]);

  const open = (sid) => { setDetail(null); setOpenId(sid); setPanel('detail'); topRef.current?.scrollIntoView({ block: 'start' }); };
  const back = () => { setPanel('list'); setOpenId(null); setDetail(null); loadMine(); onHighlightConsumed?.(); };   // Fix 7c F4
  const openBlockouts = () => { setPanel('blockouts'); setBlockoutsFailed(false); loadBlockouts(); };
  const retryMine = () => { setMineFailed(false); loadMine(); };
  const retryBlockouts = () => { setBlockoutsFailed(false); loadBlockouts(); };

  // One respond call per assignment (ruling Q1). 409 → the detail as a toast; 403 → refresh.
  const respond = async (rows, status, reason) => {
    setBusy(true);
    let ok = 0;
    try {
      for (const a of rows) {
        try {
          await svcPost(`/assignments/${a.assignment_id}/respond`, userId, { status, reason });
          ok += 1;
        } catch (err) {
          if (err?.status === 409) toast.error(errorText(err));
          else if (err?.status !== 403) toast.error(errorText(err));
        }
      }
      if (ok) toast.success(status === 'accepted' ? 'Accepted. Thanks!' : 'Declined. Your leader has been told.');
    } finally {
      setBusy(false);
      await loadMine();
      if (openId) {
        if (status === 'declined') back();
        else loadDetail(openId);
      }
    }
    return ok;
  };

  const acceptItem = (item) => respond(item.assignments.filter((a) => a.status === 'pending'), 'accepted');

  // Fix 7 G5 (ruling Q1, QUESTION 4 = A): services in list order, rows in sort
  // order (/mine already sorts them), one call after another. A failed call is
  // skipped and the rest continue; k counts services whose every pending row was
  // accepted; the toast carries the first failure's text, never a 403's (silent).
  const pendingToAccept = (mine?.pending || [])
    .map((it) => ({ item: it, rows: it.assignments.filter((a) => a.status === 'pending') }))
    .filter((x) => x.rows.length);
  // Fix 7b R3: the confirm only stands while two or more services still wait. A /mine
  // reload under it (notifStamp, focus, an answer from another device) that drops the
  // list below two closes it, and a confirm that slips through sends nothing.
  const acceptAllStands = pendingToAccept.length >= 2;
  useEffect(() => { if (acceptAllOpen && !acceptAllStands) setAcceptAllOpen(false); }, [acceptAllOpen, acceptAllStands]);
  const acceptAll = async () => {
    if (acceptingAll.current) return;
    acceptingAll.current = true;
    acceptAllRan.current = true;
    const todo = pendingToAccept;
    setAcceptAllOpen(false);
    if (todo.length < 2) {
      acceptingAll.current = false;
      setBusy(false);
      return;
    }
    setBusy(true);
    let k = 0;
    let firstFailure = null;
    try {
      for (const { rows } of todo) {
        let allOk = true;
        for (const a of rows) {
          try {
            // Fix 7c F18: never change a row someone answered elsewhere after this list loaded.
            await svcPost(`/assignments/${a.assignment_id}/respond`, userId, { status: 'accepted', only_if: 'pending' });
          } catch (err) {
            allOk = false;
            const answeredElsewhere = err?.status === 409 && err?.detail === 'This request was already answered.';   // F18: like a 403
            if (err?.status !== 403 && !answeredElsewhere && firstFailure === null) firstFailure = errorText(err);
          }
        }
        if (allOk) k += 1;
      }
      const n = todo.length;
      if (k === n) toast.success(`Accepted ${n} services. Thanks!`);
      else toast.error(firstFailure ? `Accepted ${k} of ${n}. ${firstFailure}` : `Accepted ${k} of ${n}.`);
    } finally {
      // Fix 7c F9: stay busy until /mine has returned, so the stale list can't start a second run.
      await loadMine();
      if (openIdRef.current) loadDetail(openIdRef.current);
      setBusy(false);
      acceptingAll.current = false;
      listHeadingRef.current?.focus();
    }
  };
  const askDecline = (item) => setDeclineFor({
    service_id: item.service_id, date: item.date,
    rows: item.assignments.filter((a) => a.status === 'pending' || a.status === 'accepted'),
  });
  const confirmDecline = async (reason) => {
    const d = declineFor;
    setDeclineFor(null);
    if (d) await respond(d.rows, 'declined', reason);
  };

  const addBlockout = async (range) => {
    setBusy(true);
    try {
      const out = await svcPost('/blockouts', userId, range);
      setBlockouts((prev) => [...(prev || []), out]);
      toast.success(`Blocked out ${fmtBlockoutRange(out)}`);
      const live = (out.overlaps || []).filter((o) => o.status === 'accepted' || o.status === 'pending');
      if (live.length) setOverlapAsk(live);      // B-01 / B-02: ask, never auto-decline
      return true;
    } catch (err) {
      toast.error(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const deleteBlockout = async (b) => {
    setBusy(true);
    try {
      await svcDelete(`/blockouts/${b.id}`, userId);
      setBlockouts((prev) => (prev || []).filter((x) => x.id !== b.id));
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const declineOverlaps = async () => {
    const rows = overlapAsk || [];
    setOverlapAsk(null);
    await respond(rows, 'declined', 'Blocked out');
    loadBlockouts();
  };

  const myDates = useMemo(() => [...(mine?.pending || []), ...(mine?.upcoming || [])].map((i) => i.date), [mine]);
  const empty = mine && !mine.pending.length && !mine.upcoming.length && !mine.past.length;

  let body;
  if (!resolved && !mine) {
    body = <div className="h-24 bg-slate-100 dark:bg-[#3a3d41] rounded-2xl animate-pulse" />;
  } else if (mode === 'pco') {
    body = <PcoMirror userId={userId} notifStamp={notifStamp} />;
  } else if (mode !== 'simple') {
    body = <p className="text-sm text-slate-500 dark:text-slate-400">Services is not available for your church.</p>;
  } else if (panel === 'blockouts') {
    body = (
      <div className={CARD} data-testid="services-view-blockouts">
        <div className="flex items-center gap-2 mb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 -ml-1" onClick={() => setPanel('list')} aria-label="Back" data-testid="services-view-blockouts-back">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Block out dates</h2>
        </div>
        {blockouts === null && blockoutsFailed ? (
          <LoadFailed title={'Couldn\u2019t load your blocked-out dates'} onRetry={retryBlockouts} testIdPrefix="services-view-blockouts" className="text-center py-8" />
        ) : blockouts === null ? (
          <div className="h-64 bg-slate-100 dark:bg-[#3a3d41] rounded-lg animate-pulse" />
        ) : (
          <BlockoutCalendar
            blockouts={blockouts}
            serviceDates={myDates}
            today={churchNow(detail?.church_timezone || mine?.church_timezone).date}
            onAdd={addBlockout}
            onDelete={deleteBlockout}
            busy={busy}
            intro="Your leader sees these when scheduling. You can still be asked, but they'll know you're away."
            testIdPrefix="services-view-blockout"
          />
        )}
        <ServicesFooter />
      </div>
    );
  } else if (panel === 'detail') {
    body = !detail ? (
      <div className={`${CARD} animate-pulse`} data-testid="services-view-detail-loading">
        <div className="h-6 w-48 bg-slate-200 dark:bg-[#3e4145] rounded mb-3" />
        <div className="h-4 w-64 bg-slate-100 dark:bg-[#3a3d41] rounded" />
      </div>
    ) : (() => {
      const mineRows = detail.my_assignments || [];
      const pendingRows = mineRows.filter((a) => a.status === 'pending');
      const canChange = mineRows.some((a) => a.can_change);
      const status = pendingRows.length ? 'pending' : 'accepted';
      const team = (detail.assignments || []).filter((a) => a.user_name);
      const closed = passedBy(detail, 7) ? 'Comments are closed for this service.' : null;
      return (
        <div className="space-y-4" data-testid={`services-view-detail-${detail.id}`}>
          <div className={`relative ${CARD}`}>
            {ring === detail.id && focusComments !== detail.id && <HighlightRing className="rounded-2xl" />}
            <div className="flex items-start gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8 -ml-1" onClick={back} aria-label="Back to my services" data-testid="services-view-detail-back">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">{fmtDate(detail.date, 'EEEE, MMMM d')}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{[detail.title, timesLine(detail, detail.church_timezone)].filter(Boolean).join(' · ')}</p>
              </div>
            </div>
            <div className={`mt-3 ${ROW}`} data-testid="services-view-my-card">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-700 dark:text-slate-200">You're on <span className="font-semibold">{positionsOf(mineRows)}</span></span>
                <StatusPill status={status} data-testid="services-view-my-status" />
              </div>
              {canChange && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {pendingRows.length > 0 && (
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => respond(pendingRows, 'accepted')} disabled={busy} data-testid="services-view-detail-accept">
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={() => setDeclineFor({ service_id: detail.id, date: detail.date, rows: mineRows })} disabled={busy} data-testid="services-view-detail-decline">
                    {pendingRows.length ? <><X className="w-4 h-4 mr-1" /> Decline</> : 'Change my answer'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className={CARD} data-testid="services-view-songs">
            <h3 className={HEADING}>SONGS</h3>
            {(detail.songs || []).length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No songs yet.</p>
            ) : (
              <div className="space-y-2">
                {detail.songs.map((s) => {
                  const noChart = s.key && !s.deleted && !s.has_chart_for_key;
                  return (
                    <button key={s.song_id} type="button" onClick={() => setSong(s)} className={`w-full text-left flex items-center gap-2 ${ROW}`} data-testid={`services-view-song-${s.song_id}`}>
                      <Music className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={s.title}>{s.title}</span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400 truncate" title={s.deleted ? 'song removed from library' : (s.artist || undefined)}>{s.deleted ? 'song removed from library' : (s.artist || ' ')}</span>
                      </span>
                      {noChart && <span className="text-xs font-medium text-amber-700 dark:text-amber-300 whitespace-nowrap">no chart in {s.key}</span>}
                      {s.key && <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300">{s.key}</span>}
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className={CARD} data-testid="services-view-team">
            <h3 className={HEADING}>TEAM</h3>
            <div className="space-y-2">
              {(detail.assignments || []).map((a, i) => (
                <div key={i} className={`flex items-center gap-2 ${ROW}`}>
                  <span className="min-w-0 flex-1 text-sm text-slate-800 dark:text-slate-100 truncate">{a.user_name || <span className="text-slate-500 dark:text-slate-400">Open</span>}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{a.position}</span>
                  {a.user_name && <StatusIcon status={a.status} />}
                </div>
              ))}
            </div>
          </div>

          {(detail.order || []).length > 0 && (
            <div className={CARD} data-testid="services-view-order">
              <h3 className={HEADING}>ORDER OF SERVICE</h3>
              <div className="space-y-1.5">
                {detail.order.map((o, i) => (
                  <div key={i} className="flex items-start justify-between gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <span className="min-w-0 break-words">{o.title}</span>
                    {o.minutes != null && <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{o.minutes} min</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.notes && (
            <div className={CARD} data-testid="services-view-notes">
              <h3 className={HEADING}>NOTES</h3>
              <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">{detail.notes}</p>
            </div>
          )}

          <div ref={commentsRef} className={`relative ${CARD}`} data-testid="services-view-comments">
            {focusComments === detail.id && <HighlightRing className="rounded-2xl" />}
            <h3 className={HEADING} data-testid="services-view-comments-heading">COMMENTS{detail.comments_count && !commentsFailed ? ` (${detail.comments_count})` : ''}</h3>
            <ServiceComments
              serviceId={detail.id}
              userId={userId}
              isAdmin={false}
              teamCount={team.length}
              onLoadFailedChange={setCommentsFailed}
              closedReason={closed}
              reloadKey={notifStamp}
            />
          </div>
          <ServicesFooter />
        </div>
      );
    })();
  } else {
    const section = (title, items, pending, testid, action = null) => items.length > 0 && (
      <div data-testid={testid}>
        {action ? (
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className={HEADING.replace(' mb-3', '')}>{title}</h3>
            {action}
          </div>
        ) : (
          <h3 className={HEADING}>{title}</h3>
        )}
        <div className="space-y-3">
          {items.map((it) => (
            <MineCard key={it.service_id} item={it} pending={pending} busy={busy} onOpen={open} onAccept={acceptItem} onDecline={askDecline} highlight={ring === it.service_id} tz={mine?.church_timezone} />
          ))}
        </div>
      </div>
    );
    body = mine === null && mineFailed ? (
      <LoadFailed title={'Couldn\u2019t load your services'} onRetry={retryMine} testIdPrefix="services-view-list" className={`${CARD} text-center py-8`} />
    ) : mine === null ? (
      <div className="space-y-3">
        {[0, 1].map((i) => <div key={i} className="h-28 bg-slate-100 dark:bg-[#3a3d41] rounded-2xl animate-pulse" />)}
      </div>
    ) : (
      <div className="space-y-6">
        {isAdmin && (
          <p className="text-sm text-slate-500 dark:text-slate-400 flex items-start gap-1.5" data-testid="services-view-admin-note">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> You're an admin. Plan services in the Services app on your dashboard. Here you see your own schedule like everyone else.
          </p>
        )}
        {empty ? (
          <div className={`${CARD} text-center py-8`} data-testid="services-view-empty">
            <p className="text-base font-semibold text-slate-800 dark:text-slate-100">Nothing scheduled yet</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">When your leader schedules you, it shows up here and you'll get a notification to accept or decline.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={openBlockouts} data-testid="services-view-empty-blockouts">
              <Calendar className="w-4 h-4 mr-1.5" /> Block out dates you're away
            </Button>
          </div>
        ) : (
          <>
            {section('NEEDS YOUR ANSWER', mine.pending, true, 'services-view-pending', mine.pending.length >= 2 ? (
              <Button ref={acceptAllBtnRef} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setAcceptAllOpen(true)} disabled={busy} data-testid="services-view-accept-all">
                <Check className="w-4 h-4 mr-1" /> Accept all ({mine.pending.length})
              </Button>
            ) : null)}
            {section('COMING UP', mine.upcoming, false, 'services-view-upcoming')}
            {mine.past.length > 0 && (
              <div data-testid="services-view-past">
                <button type="button" onClick={() => setShowPast((v) => !v)} className={`${HEADING} flex items-center gap-1`} data-testid="services-view-past-toggle">
                  PAST ({mine.past.length}) <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showPast ? 'rotate-90' : ''}`} />
                </button>
                {showPast && (
                  <div className="space-y-3">
                    {mine.past.map((it) => (
                      <MineCard key={it.service_id} item={it} pending={false} busy onOpen={open} onAccept={() => {}} onDecline={() => {}} tz={mine?.church_timezone} />
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={openBlockouts} data-testid="services-view-blockouts-btn">
              <Calendar className="w-4 h-4 mr-1.5" /> Block out dates
            </Button>
          </>
        )}
        <ServicesFooter />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-57px-64px)] md:h-[calc(100vh-57px)] w-full max-w-full overflow-y-auto bg-slate-50 dark:bg-[#1e1f22]" data-testid="services-view">
      <div ref={topRef} className="max-w-2xl mx-auto px-4 py-4">
        {panel === 'list' && (
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/25">
              <Music className="w-4 h-4 text-white" />
            </div>
            <h2 ref={listHeadingRef} tabIndex={-1} className="text-lg font-semibold text-slate-800 dark:text-slate-100 focus:outline-none">My services</h2>
          </div>
        )}
        {body}
      </div>

      <DeclineDialog item={declineFor} busy={busy} onConfirm={confirmDecline} onClose={() => setDeclineFor(null)} />
      <SongCard song={song} serviceDate={detail?.date} onClose={() => setSong(null)} />

      <AlertDialog open={acceptAllOpen && acceptAllStands} onOpenChange={(o) => { if (!o) setAcceptAllOpen(false); }}>
        <AlertDialogContent
          data-testid="services-view-accept-all-dialog"
          onCloseAutoFocus={(e) => {
            // Fix 7c F17: this dialog has no Trigger, so Radix would drop focus on the page.
            // After a confirm it goes to the list heading (the button is disabled now and
            // gone once the list reloads); after Not now / Escape, back to the button.
            e.preventDefault();
            const ran = acceptAllRan.current;
            acceptAllRan.current = false;
            (ran ? listHeadingRef.current : (acceptAllBtnRef.current || listHeadingRef.current))?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Accept all {pendingToAccept.length} services?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div data-testid="services-view-accept-all-lines">
                {pendingToAccept.map(({ item, rows }) => (
                  <div key={item.service_id}>{fmtDate(item.date, 'EEE, MMM d')} · {positionsOf(rows)}</div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="services-view-accept-all-cancel">Not now</AlertDialogCancel>
            <AlertDialogAction className="bg-teal-600 hover:bg-teal-700 text-white" onClick={(e) => { e.preventDefault(); acceptAll(); }} disabled={busy} data-testid="services-view-accept-all-confirm">
              Accept all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!overlapAsk} onOpenChange={(o) => { if (!o) setOverlapAsk(null); }}>
        <AlertDialogContent data-testid="services-view-overlap-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {overlapGroups.length !== 1
                ? `You're on ${overlapGroups.length} services in those dates. Decline them too?`
                : overlapGroups[0].status === 'on'   // PC8: mixed statuses on one service
                  ? `You're on ${overlapGroups[0].positions.join(' and ')} for ${fmtDate(overlapGroups[0].date, 'MMM d')}. Decline it too?`
                  : `You're ${overlapGroups[0].status} for ${fmtDate(overlapGroups[0].date, 'MMM d')}. Decline it too?`}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="services-view-overlap-entries">
              {overlapGroups.map(overlapEntry).join('; ')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="services-view-overlap-keep">Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={declineOverlaps} disabled={busy} data-testid="services-view-overlap-decline">
              Yes, decline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
